import type { ReactNode } from 'react';
import { Explain } from '@/ui/Explain';

/**
 * A named group of fields inside a long form.
 *
 * The exercise form asks a lot, so the headings are questions a coach is
 * actually answering — "Why are we doing this?" rather than "Metadata" — which
 * keeps a long form feeling like thinking rather than data entry.
 */
export function Section({
  title,
  hint,
  explain,
  children,
}: {
  title: string;
  hint?: string;
  /**
   * Knowledge-layer term, shown as a "?" beside the heading.
   *
   * `Panel` has had this from the start; a `Section` could not carry one, which
   * is why ten of the eighteen glossary terms had no anchor anywhere — most of
   * them name a field inside a form rather than a panel.
   */
  explain?: string;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-line px-4 py-5 first:border-t-0">
      <h3 className="flex flex-wrap items-center gap-x-3 font-condensed text-subhead font-semibold tracking-tight">
        {title}
        {explain ? <Explain term={explain} /> : null}
      </h3>
      {hint ? <p className="mt-1 max-w-[72ch] text-meta text-ink-muted">{hint}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** A labelled value in a read-only detail view. */
export function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-meta text-ink-faint">{label}</dt>
      <dd className="mt-0.5 whitespace-pre-line break-words">{children}</dd>
    </div>
  );
}
